import React from "react";
import PropTypes from "prop-types";

import { useThemeA } from "../../internal/ThemeAContext";
import { ActionMenu } from "./ActionMenu";
import { IconAction } from "./IconAction";

// Clic principal sans touche de modification : Ctrl/Cmd/Maj+clic garde le geste natif du lien.
const isPlainPrimaryClick = (event) => !(
  event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
  || (event.button !== undefined && event.button !== 0)
);

/**
 * Actions d'une ligne de liste ou d'une carte (règle 8 de CLAUDE.md) : au plus
 * UNE action rapide visible (`quickAction`, icône nommée), puis le menu
 * « ••• » compact (`menu`, groupes nommés). Supprimer ou archiver n'est jamais
 * une icône visible : `intent: "destructive"` dans le menu, qui la place en
 * dernier, en rouge ; le libellé finit par « … » et ouvre une confirmation.
 * La ligne elle-même ouvre la fiche : pas d'icône « Ouvrir ».
 *
 * Action rapide qui mène ailleurs : `href` (vrai lien) + `onOpen` — un clic
 * simple passe par `onOpen` (navigation de l'application), Ctrl/Cmd+clic et le
 * clic du milieu gardent le geste natif du navigateur.
 */
export function RowActions({ menu = [], menuLabel, quickAction = null }) {
  useThemeA("RowActions");
  if (quickAction?.intent === "destructive") {
    throw new Error("RowActions: a destructive action belongs in the menu, never as a visible icon");
  }
  const hasMenu = menu.some((group) => (group.items || []).length > 0);
  if (!quickAction && !hasMenu) return null;

  return (
    <span className="akv:inline-flex akv:flex-none akv:items-center akv:justify-end akv:gap-1.5" data-ak-v2-component="row-actions">
      {quickAction ? (
        <IconAction
          disabled={quickAction.disabled}
          href={quickAction.href}
          icon={quickAction.icon}
          label={quickAction.label}
          loading={quickAction.loading}
          loadingLabel={quickAction.loadingLabel}
          onClick={quickAction.href && quickAction.onOpen ? (event) => {
            if (!isPlainPrimaryClick(event)) return;
            event.preventDefault();
            quickAction.onOpen(event);
          } : quickAction.onClick}
        />
      ) : null}
      {hasMenu ? <ActionMenu groups={menu} label={menuLabel} size="compact" /> : null}
    </span>
  );
}

RowActions.propTypes = {
  menu: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      disabled: PropTypes.bool,
      icon: PropTypes.node,
      id: PropTypes.string.isRequired,
      intent: PropTypes.oneOf(["default", "destructive"]),
      label: PropTypes.string.isRequired,
      onSelect: PropTypes.func,
    })).isRequired,
    label: PropTypes.string,
  })),
  menuLabel: PropTypes.string,
  quickAction: PropTypes.shape({
    disabled: PropTypes.bool,
    href: PropTypes.string,
    icon: PropTypes.node.isRequired,
    id: PropTypes.string.isRequired,
    intent: PropTypes.oneOf(["quiet"]),
    label: PropTypes.string.isRequired,
    // En cours : l'icône devient un indicateur, `loadingLabel` est lu.
    loading: PropTypes.bool,
    loadingLabel: PropTypes.string,
    onClick: PropTypes.func,
    // Avec `href` : navigation de l'application sur un clic simple.
    onOpen: PropTypes.func,
  }),
};

export default RowActions;
